import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {archiveAnalysis, unarchiveAnalysis} from 'analysis-tool/AnalysisActions';
import {ARCHIVED_ANALYSIS_BANNER_TEXT} from 'analysis-tool/AnalysisToolConstants';
import {
  ADVANCED_ANALYSIS_ACCESS_MESSAGE,
  isAnalysisTemplatesEnabled,
} from 'analysis-tool/AnalysisToolFeatureGates';
import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import CloneAnalysisModal from 'analysis-tool/CloneAnalysisModal';
import isAnalysisRunning from 'analysis-tool/isAnalysisRunning';
import isAnalysisSucceeded from 'analysis-tool/isAnalysisSucceeded';
import AnalysisMetadataModal from 'analysis-tool/ui/metadata-modal/AnalysisMetadataModal';
import ArchiveModal, {ArchiveOptionsForModal} from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {InlineBannerActionType, InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import {Header1} from 'ui-library/Headers';
import OverflowMenu, {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import runModal from 'ui-library/runModal';
import removeNulls from 'util/removeNulls';

import {AnalysisUIHeaderContentProps} from './AnalysisUIHeaderTypes';
import {AnalysisMainViewItem, AnalysisMainViewItemType} from './main-view/MainViewTypes';
import ToggleAnalysisSidebarButton from './sidebar/ToggleAnalysisSidebarButton';

type ArchivedBannerItemType = 'analysis';

const ITEM_TYPE_TO_BANNER_TEXT: Record<ArchivedBannerItemType, string> = {
  analysis: ARCHIVED_ANALYSIS_BANNER_TEXT,
};

function getCopyAnalysisDisabledMessage(analysis: AnalysisGraphQL): string | undefined {
  if (isAnalysisRunning(analysis)) {
    return 'Cannot duplicate analysis while it is running.';
  }

  return undefined;
}

function getCreateTemplateIsDisabledMessage(analysis: AnalysisGraphQL): string | undefined {
  if (!isAnalysisTemplatesEnabled()) {
    return ADVANCED_ANALYSIS_ACCESS_MESSAGE;
  } else if (!isAnalysisSucceeded(analysis)) {
    return 'Cannot create template from an analysis that has not succeeded.';
  } else if (analysis.execution.steps.length === 0) {
    return 'Cannot create template from an empty analysis.';
  } else if (
    analysis.execution.steps.some((step) => step.definition.stepType === PipelineStepType.FLAG_ROWS)
  ) {
    return 'Analysis with flagged rows cannot be used to create a template.';
  }

  return undefined;
}

function getApplyTemplateIsDisabledMessage(analysis: AnalysisGraphQL): string | undefined {
  if (!isAnalysisTemplatesEnabled()) {
    return ADVANCED_ANALYSIS_ACCESS_MESSAGE;
  } else if (!analysis.permissions.writable) {
    return 'You do have access to modify this analysis.';
  }

  return undefined;
}

function DefaultAnalysisUIHeaderContent({
  analysis,
  showOpenSidebarButton,
  onChangeSelectedItem,
  onOpenSidebar,
  onOpenStepInitializationView,
}: AnalysisUIHeaderContentProps): JSX.Element {
  const overflowMenuOptions: Array<OverflowMenuOption> = removeNulls([
    {
      icon: 'template',
      isDisabled: getCreateTemplateIsDisabledMessage(analysis) != null,
      tooltipTitle: getCreateTemplateIsDisabledMessage(analysis),
      contents: <span>Save as analysis template</span>,
      onSelect: () => {
        onOpenStepInitializationView({
          type: AnalysisMainViewItemType.TEMPLATE_CONFIGURATION,
        });
      },
      dataTestAttrs: {
        component: 'AnalysisUIHeader',
        element: 'saveAsAnalysisTemplateOption',
      },
    },
    {
      icon: 'template',
      isDisabled: getApplyTemplateIsDisabledMessage(analysis) != null,
      tooltipTitle: getApplyTemplateIsDisabledMessage(analysis),
      contents: <span>Apply analysis template</span>,
      onSelect: () => {
        onOpenStepInitializationView({
          type: AnalysisMainViewItemType.TEMPLATE_APPLY,
        });
      },
      dataTestAttrs: {
        component: 'AnalysisUIHeader',
        element: 'applyAnalysisTemplateOption',
      },
    },
    {
      icon: 'copy',
      isDisabled: getCopyAnalysisDisabledMessage(analysis) != null,
      tooltipTitle: getCopyAnalysisDisabledMessage(analysis),
      contents: <span>Duplicate</span>,
      onSelect: () => {
        runModal(CloneAnalysisModal, {analysisToClone: analysis});
      },
    },
    {
      icon: 'audit-trail',
      contents: <span>{EXPORT_AUDIT_LOG_DISPLAY_STRING}</span>,
      onSelect: () => {
        exportAuditLogs([analysis.id]);
      },
    },
    analysis.permissions.archivable
      ? {
          icon: 'archive',
          isDisabled: isAnalysisRunning(analysis),
          tooltipTitle: isAnalysisRunning(analysis)
            ? 'Cannot archive analysis while it is running'
            : undefined,
          contents: <span>Archive analysis</span>,
          onSelect: () => {
            runModal(ArchiveModal, {
              items: [analysis],
              onArchive: (
                _itemIds: Array<string>,
                purpose: string,
                options: ArchiveOptionsForModal
              ): void => {
                archiveAnalysis(analysis.id, purpose, options.resultPurpose ?? null);
              },
            });
          },
        }
      : null,
  ]);

  return (
    <>
      <div className={css(styles.leftContentWrapper)}>
        {showOpenSidebarButton && (
          <div className={css(styles.expandSidebarWrapper)}>
            <ToggleAnalysisSidebarButton sidebarIsOpen={false} toggleSidebar={onOpenSidebar} />
          </div>
        )}
        <Header1 className={css(styles.analysisName)}>{analysis.name}</Header1>
      </div>
      <div className={css(styles.rightContentWrapper)}>
        {!(
          analysis.permissions.writable ||
          analysis.permissions.unarchivable ||
          analysis.permissions.archivable
        ) && (
          <Tooltip title={READ_ONLY_TOOLTIP_TEXT} placement={TooltipPlacement.BOTTOM}>
            <span className={css(styles.readOnly)}>Read-only</span>
          </Tooltip>
        )}
        <AnalysisFlowchartButton onChangeSelectedItem={onChangeSelectedItem} />
        <AnalysisCreateOutputsButton onChangeSelectedItem={onChangeSelectedItem} />
        <AnalysisMetadataButton
          buttonSize={ButtonSize.MINI}
          tooltipPlacement={TooltipPlacement.LEFT}
          analysis={analysis}
        />
        <OverflowMenu
          buttonProps={{
            size: ButtonSize.MINI,
            buttonStyle: ButtonStyle.TERTIARY_GRAY,
            dataTestAttrs: {component: 'AnalysisUIHeader', element: 'overflowMenu'},
          }}
          enableTooltip={true}
          menuProps={{dropLeft: true}}
          options={overflowMenuOptions}
          tooltipPlacement={FloatingPlacement.LEFT}
        />
      </div>
    </>
  );
}

export interface AnalysisUIHeaderProps extends AnalysisUIHeaderContentProps {
  analysis: AnalysisGraphQL;
  selectedItem: AnalysisMainViewItem;
}

function getBannerStackMessages({
  analysis,
  actions,
}: {
  analysis: AnalysisGraphQL;
  actions: {
    unarchive?: () => Promise<void>;
    onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void;
  };
}): Array<InlineBannerProps> {
  const messages: Array<InlineBannerProps> = [];
  if (isArchived(analysis)) {
    messages.push({
      bannerType: InlineBannerType.WARNING,
      icon: 'box-archive',
      action:
        actions.unarchive != null
          ? {
              actionType: InlineBannerActionType.CUSTOM,
              actionText: 'Unarchive',
              actionOnClick: actions.unarchive,
            }
          : undefined,
      text: `This analysis is archived (${analysis.archiveRecord.purpose}).`,
      supportingInfo: actions.unarchive != null ? ITEM_TYPE_TO_BANNER_TEXT.analysis : undefined,
      dataTestAttrs: {component: 'ArchivedBanner', element: 'root'},
    });
  }
  return messages;
}

/** @package */
export const READ_ONLY_TOOLTIP_TEXT = 'For edit access please contact the project owner.';

export default function AnalysisUIHeader({
  selectedItem,
  ...headerContentProps
}: AnalysisUIHeaderProps): JSX.Element {
  const {analysis} = headerContentProps;

  const bannerStackMessages = getBannerStackMessages({
    analysis,
    actions: {
      unarchive: analysis.permissions.unarchivable
        ? async () => {
            await unarchiveAnalysis(analysis.id);
          }
        : undefined,
      onOpenStepInitializationView: headerContentProps.onOpenStepInitializationView,
    },
  });

  return (
    <div {...getTestProps({component: 'AnalysisUIHeader', element: 'root'})}>
      {bannerStackMessages.length > 0 && (
        <div className={css(styles.bannerStack)}>
          <InlineBannerStack messages={bannerStackMessages} />
        </div>
      )}
      <div className={css(styles.contentWrapper)}>
        <DefaultAnalysisUIHeaderContent {...headerContentProps} />
      </div>
    </div>
  );
}

interface AnalysisMetadataButtonProps {
  buttonSize: ButtonSize;
  tooltipPlacement: TooltipPlacement;
  analysis: AnalysisGraphQL;
}

export function AnalysisMetadataButton(props: AnalysisMetadataButtonProps): JSX.Element {
  function handleClick(): void {
    runModal(AnalysisMetadataModal, {analysis: props.analysis});
  }

  return (
    <Tooltip title="View metadata" placement={props.tooltipPlacement}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        icon="info-circle"
        size={props.buttonSize}
        onClick={handleClick}
        className="u-mini-spacing-left"
        dataTestAttrs={{component: 'AnalysisMetadataButton', element: 'root'}}
      />
    </Tooltip>
  );
}

function AnalysisCreateOutputsButton({
  onChangeSelectedItem,
}: Pick<AnalysisUIHeaderContentProps, 'onChangeSelectedItem'>): JSX.Element {
  return (
    <Button
      buttonStyle={ButtonStyle.TERTIARY_BLUE}
      icon="file-import"
      size={ButtonSize.MINI}
      text="Create outputs"
      onClick={() => {
        onChangeSelectedItem({type: AnalysisMainViewItemType.CREATE_OUTPUTS});
      }}
      className={css(styles.headerButton)}
      dataTestAttrs={{component: 'AnalysisCreateOutputsButton', element: 'root'}}
    />
  );
}

function AnalysisFlowchartButton({
  onChangeSelectedItem,
}: Pick<AnalysisUIHeaderContentProps, 'onChangeSelectedItem'>): JSX.Element {
  return (
    <Button
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      icon="flowchart"
      size={ButtonSize.MINI}
      text="View flowchart"
      onClick={() => {
        onChangeSelectedItem({type: AnalysisMainViewItemType.FLOWCHART});
      }}
      className={css(styles.headerButton)}
    />
  );
}

const styles = StyleSheet.create({
  contentWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftContentWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  rightContentWrapper: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    paddingLeft: 6,
  },
  expandSidebarWrapper: {
    marginRight: 12,
  },
  analysisName: {
    // Helps align the name with the Expand Sidebar button
    marginTop: 3,
  },
  bannerStack: {
    marginBottom: 15,
  },
  headerButton: {
    marginLeft: 6,
    flexShrink: 0,
  },
  readOnly: {
    ...FONT_PROXIMA_TITLE,
    color: GRAY_DARK,
  },
});
